import { ApiConnector } from './api.connector';
import { leasingRequestMapperFromApi } from './services/requestsDataAdapter';
import type {
  IAcceptResponseBody,
  ICancelResponseBody,
  ILeasingRequestInfo,
  IRequestInfo
} from './types/requests.types';

interface ICancelRequestBody {
  requestId: string;
  reason: string;
}

export interface IBase64FilePayload {
  name: string;
  base64: string;
}

export interface IUploadDocumentsBody {
  requestId: string;
  docPhoto?: IBase64FilePayload;
}

export interface IRequestItem {
  uuid: string;
  code: string | null;
  create_date: string;
  update_date: string | null;
  vehicle_cost: string;
  car_year: string;
  status: {
    uuid: string;
    name: string;
    code: string;
  };
  model: {
    uuid: string;
    name: string;
    img_src: string;
    brand: {
      name: string;
      img_src: string;
    };
  };
  Responses: Array<{
    uuid: string;
    bank: {
      uuid: string;
      code: string;
      name: string;
      fullname: string;
      icon?: string;
    };
    status: {
      uuid: string;
      name: string;
      code: string;
    };
  }>;
  equipment?: {
    uuid: string;
    name: string;
  };
}

export interface ILeasingItem {
  uuid: string;
  code: string | null;
  create_date: string;
  update_date: string | null;
  request_amount: number;
  status: {
    uuid: string;
    name: string;
    code: string;
  };
  equipment: {
    name: string;
    model: {
      name: string;
      img_src: string;
      brand: {
        name: string;
        img_src: string;
      };
    };
  };
  responses: Array<{
    status: {
      name: string;
      code: string;
    };
    Leasing_Company?: {
      name: string;
      icon: string;
    };
  }>;
}

class RequestsApi extends ApiConnector {
  cancelRequest(body: ICancelRequestBody): Promise<void> {
    return this.call({
      path: 'online-requests/cancel',
      method: 'post',
      body
    });
  }

  async uploadDocuments(body: IUploadDocumentsBody): Promise<void> {
    return this.call<IUploadDocumentsBody, void>({
      path: 'online-requests/upload-documents',
      method: 'post',
      body
    });
  }

  async finalizeRequest(requestId: string): Promise<void> {
    return this.call<{ requestId: string }, void>({
      path: 'online-requests/finalize',
      method: 'post',
      body: { requestId }
    });
  }

  async getMyRequests(
    dealerGroupId: string
  ): Promise<{ requests: IRequestItem[] }> {
    return this.call<never, { requests: IRequestItem[] }>({
      path: `online-clients/${dealerGroupId}`,
      method: 'get'
    });
  }

  async getMyLeasings(
    leasingId: string
  ): Promise<{ requests: ILeasingItem[] }> {
    const response = await this.call<
      never,
      { leasings: ILeasingItem[]; total: number }
    >({
      path: `online-clients/${leasingId}/leasings`,
      method: 'get'
    });
    return { requests: response.leasings };
  }

  async getRequestInfo(requestId: string): Promise<IRequestInfo> {
    return this.call({
      path: `online-clients/request/${requestId}`,
      method: 'get'
    });
  }

  async getLeasingRequestInfo(requestId: string): Promise<IRequestInfo> {
    const response = await this.call<never, ILeasingRequestInfo>({
      path: `online-clients/leasing-request/${requestId}`,
      method: 'get'
    });
    return leasingRequestMapperFromApi(response);
  }

  async acceptResponse(body: IAcceptResponseBody): Promise<void> {
    return this.call({
      path: 'online-clients/accept-offer',
      method: 'post',
      body
    });
  }

  async cancelResponse(body: ICancelResponseBody): Promise<void> {
    return this.call({
      path: 'online-clients/decline-offer',
      method: 'post',
      body
    });
  }

  async printCommercialDocument(requestId: string): Promise<string> {
    return this.call({
      path: `requests/commercial_offer/${requestId}`,
      method: 'get'
    });
  }
}

export const requestsApi = new RequestsApi();
